import type { DirectusFile } from '../types/DirectusFile.ts';
import type { DirectusResponse } from '../types/DirectusResponse.ts';
import { ref, type Ref } from 'vue';
import { CacheService } from './CacheService.ts';
import { createDirectus, rest } from '@directus/sdk';

export const API_URL = 'https://db.retry.li';

const ICON_FOLDER = '1bf7b5aa-7b1a-45bc-84a4-5a9071180743';
const CHARACTER_FOLDER = 'be4f6080-707c-4976-b27d-3aa887f4cbf1';

export const directus = createDirectus(API_URL).with(rest());

let cachedFiles: DirectusFile[] | null = null;

export const icons: Ref<DirectusFile[]> = ref([]);
export const characters: Ref<DirectusFile[]> = ref([]);
export const isDirectusReady: Ref<boolean> = ref(false);
export const CHARACTER_DEFAULT: string = 'edb0fbff-2a36-429f-9933-a65d41497f0d';
export const ICON_PLUS: string = '6d6a3d9f-29df-49de-84c6-42478e2c9fb8';
export const ICON_MINUS: string = 'a0a3d67b-e641-4bc8-8d6b-efecefa1f95a';
export const ICON_EMPTY: string = 'aa8b86a6-b04c-402a-af22-b6c7ea705210';
export const ICON_PEN: string = 'af0bdd82-67a0-46bb-9f97-2eda6b785ee6';
export const ICON_SAVE: string = '8c25551f-3eb2-4490-a5e1-23b448e34133';

const cache = new CacheService();

export async function getFiles(useCache = true): Promise<DirectusFile[]> {
  if (useCache && cachedFiles) return cachedFiles;

  const cached = await cache.get<DirectusResponse<DirectusFile>>('files');
  let json: DirectusResponse<DirectusFile> | null = cached.value;

  if (!json || cached.isExpired) {
    try {
      const res = await fetch(`${API_URL}/files?sort=created_on&limit=-1`);
      if (!res.ok) throw new Error('Failed to fetch files');
      json = await res.json();
      await cache.set<DirectusResponse<DirectusFile>>(
        'files',
        json as DirectusResponse<DirectusFile>,
        60 * 60 * 1000 * 24 * 3
      );
    } catch (e) {
      console.warn(
        'Failed to refresh files from Directus, falling back to cached value if available.',
        e
      );
      if (!json) {
        throw e; // no cached value to fall back to
      }
    }
  }

  cachedFiles = (json as DirectusResponse<DirectusFile>).data;

  icons.value = cachedFiles.filter((e) => e.folder === ICON_FOLDER);
  characters.value = cachedFiles.filter((e) => e.folder === CHARACTER_FOLDER);

  isDirectusReady.value = true;

  return cachedFiles;
}

export function getFileUrl(fileId: string): string {
  return `${API_URL}/assets/${fileId}?width=512`;
}

// Upload a file to Directus and return the created file ID
export async function uploadFileToFolder(
  file: Blob,
  filename: string,
  folderId: string
): Promise<string> {
  const form = new FormData();
  form.append('folder', folderId);
  form.append('file', file, filename);

  const res = await fetch(`${API_URL}/files`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    throw new Error('Failed to upload file to Directus');
  }
  const json = await res.json();
  // Response shape typically: { data: { id: '...' , ... } }
  const id = json?.data?.id;
  if (!id) {
    throw new Error('Directus upload succeeded but no file id returned');
  }
  return id as string;
}
